/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BillCreate } from '../models/BillCreate';
import type { BillDetails } from '../models/BillDetails';
import type { BillDetailsMinimum } from '../models/BillDetailsMinimum';
import type { BillUpdate } from '../models/BillUpdate';
import type { BillUsageDetails } from '../models/BillUsageDetails';
import type { PagedItems } from '../models/PagedItems';
import type { PagingQuery } from '../models/PagingQuery';
import type { StoredObjectDetailsDTO } from '../models/StoredObjectDetailsDTO';
import type { UsageDetailsMinimum } from '../models/UsageDetailsMinimum';
import { request as __request } from '../core/request';

export class BillsVService {

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns PagedItems OK
     * @throws ApiError
     */
    public static async billsVGetAll(
        billingAccountId: string,
        requestBody?: PagingQuery,
    ): Promise<PagedItems> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/get`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns BillDetails OK
     * @throws ApiError
     */
    public static async billsVGetExport(
        billingAccountId: string,
        requestBody?: PagingQuery,
    ): Promise<BillDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/export`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns BillDetailsMinimum OK
     * @throws ApiError
     */
    public static async billsVGetPdfExport(
        billingAccountId: string,
        requestBody?: PagingQuery,
    ): Promise<BillDetailsMinimum> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/export/pdf`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns BillDetails OK
     * @throws ApiError
     */
    public static async billsVGetPastTwoYearsExport(
        billingAccountId: string,
    ): Promise<BillDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/export/pdf`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns BillUsageDetails OK
     * @throws ApiError
     */
    public static async billsVGetAllUsages(
        billingAccountId: string,
    ): Promise<Array<BillUsageDetails>> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/get/usages`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns BillUsageDetails OK
     * @throws ApiError
     */
    public static async billsVGetUsagesExport(
        billingAccountId: string,
    ): Promise<BillUsageDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/export/usages`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns UsageDetailsMinimum OK
     * @throws ApiError
     */
    public static async billsVGetUsagesPdfExport(
        billingAccountId: string,
    ): Promise<UsageDetailsMinimum> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/export/usages/pdf`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billId
     * @param billingAccountId
     * @param requestBody
     * @returns BillDetails OK
     * @throws ApiError
     */
    public static async billsVUpdateBill(
        billId: string,
        billingAccountId: string,
        requestBody?: BillUpdate,
    ): Promise<BillDetails> {
        const result = await __request({
            method: 'PATCH',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/${billId}`,
            body: requestBody,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param billId
     * @returns BillDetails OK
     * @throws ApiError
     */
    public static async billsVGetById(
        billingAccountId: string,
        billId: string,
    ): Promise<BillDetails> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/${billId}`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param billId
     * @returns any OK
     * @throws ApiError
     */
    public static async billsVDelete(
        billingAccountId: string,
        billId: string,
    ): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/${billId}`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param requestBody
     * @returns BillDetails Created
     * @throws ApiError
     */
    public static async billsVCreate(
        billingAccountId: string,
        requestBody?: BillCreate,
    ): Promise<BillDetails> {
        const result = await __request({
            method: 'POST',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills`,
            body: requestBody,
            errors: {
                400: `Bad Request`,
                401: `Unauthorized`,
                403: `Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @returns BillDetails OK
     * @throws ApiError
     */
    public static async billsVGetPastTwoYears(
        billingAccountId: string,
    ): Promise<Array<BillDetails>> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills`,
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param billId
     * @returns StoredObjectDetailsDTO OK
     * @throws ApiError
     */
    public static async billsVGetAttachment(
        billingAccountId: string,
        billId: string,
    ): Promise<StoredObjectDetailsDTO> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/${billId}/attachment`,
            errors: {
                401: `Unauthorized`,
                403: `Forbidden`,
                404: `Not Found`,
            },
        });
        return result.body;
    }

    /**
     * @param billingAccountId
     * @param billingCycleId
     * @returns boolean OK
     * @throws ApiError
     */
    public static async billsVHasBills(
        billingAccountId: string,
        billingCycleId?: string,
    ): Promise<boolean> {
        const result = await __request({
            method: 'GET',
            path: `/api/v2/billing_accounts/${billingAccountId}/bills/hasbills`,
            query: {
                'billingCycleId': billingCycleId,
            },
        });
        return result.body;
    }

}